import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Key, Plus, Trash2, Copy, Eye, EyeOff, RefreshCw, 
  Shield, Clock, Activity, AlertTriangle, Check, X,
  Code, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const APIKeysPage = () => {
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [showDocs, setShowDocs] = useState(false);
  const [availableScopes, setAvailableScopes] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    rate_limit: 1000,
    expires_in_days: null
  });

  useEffect(() => {
    loadApiKeys();
    loadScopes();
  }, []);

  const loadApiKeys = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(response.data || []);
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const loadScopes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api-keys/scopes/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableScopes(response.data || {});
    } catch (error) {
      console.error('Failed to load scopes');
    }
  };

  const handleCreateKey = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api-keys/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show the new key
      setNewApiKey(response.data.api_key);
      setNewKeyDialogOpen(true);
      setCreateDialogOpen(false);
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        permissions: [],
        rate_limit: 1000,
        expires_in_days: null
      });
      
      loadApiKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? It will no longer work.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api-keys/${keyId}/revoke`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('API key revoked');
      loadApiKeys();
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };

  const handleActivateKey = async (keyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api-keys/${keyId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('API key activated');
      loadApiKeys();
    } catch (error) {
      toast.error('Failed to activate API key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to permanently delete this API key? This cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('API key deleted');
      loadApiKeys();
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const togglePermission = (scope) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(scope)
        ? prev.permissions.filter(p => p !== scope)
        : [...prev.permissions, scope]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-playfair text-wt-text-primary">
            API Keys
          </h1>
          <p className="text-wt-text-secondary text-sm sm:text-base">
            Manage API keys for external integrations
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowDocs(!showDocs)}
            className="border-wt-border text-wt-text-muted"
          >
            <Code className="h-4 w-4 mr-2" />
            {showDocs ? 'Hide' : 'Show'} Docs
          </Button>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Key
          </Button>
        </div>
      </div>

      {/* API Documentation */}
      {showDocs && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-wt-paper rounded-lg border border-wt-border p-4"
        >
          <h3 className="text-lg font-semibold text-wt-text-primary mb-3">API Documentation</h3>
          
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium text-wt-gold mb-2">Authentication</h4>
              <p className="text-wt-text-muted mb-2">Include your API key in requests using one of these methods:</p>
              <div className="bg-wt-bg rounded p-3 font-mono text-xs">
                <p className="text-green-400"># Header method (recommended)</p>
                <p className="text-wt-text-primary">X-API-Key: wt_live_xxxxx</p>
                <p className="text-green-400 mt-2"># Bearer token method</p>
                <p className="text-wt-text-primary">Authorization: Bearer wt_live_xxxxx</p>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-wt-gold mb-2">Base URL</h4>
              <div className="bg-wt-bg rounded p-3 font-mono text-xs text-wt-text-primary">
                {process.env.REACT_APP_BACKEND_URL}/api/external
              </div>
            </div>

            <div>
              <h4 className="font-medium text-wt-gold mb-2">Available Endpoints</h4>
              <div className="grid gap-2">
                {[
                  { method: 'GET', path: '/orders', desc: 'List orders' },
                  { method: 'GET', path: '/orders/{id}', desc: 'Get order details' },
                  { method: 'PUT', path: '/orders/{id}/status', desc: 'Update order status' },
                  { method: 'GET', path: '/inventory', desc: 'List inventory' },
                  { method: 'PUT', path: '/inventory/{sku}/quantity', desc: 'Update stock' },
                  { method: 'GET', path: '/customers', desc: 'List customers' },
                  { method: 'POST', path: '/customers', desc: 'Create customer' },
                  { method: 'GET', path: '/products', desc: 'List products' },
                  { method: 'GET', path: '/purchase-orders', desc: 'List POs' },
                  { method: 'GET', path: '/leads', desc: 'List leads' },
                  { method: 'POST', path: '/leads', desc: 'Create lead' },
                ].map((endpoint, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs">
                    <Badge className={`${
                      endpoint.method === 'GET' ? 'bg-blue-500/20 text-blue-400' :
                      endpoint.method === 'POST' ? 'bg-green-500/20 text-green-400' :
                      'bg-orange-500/20 text-orange-400'
                    } font-mono`}>
                      {endpoint.method}
                    </Badge>
                    <code className="text-wt-text-primary">{endpoint.path}</code>
                    <span className="text-wt-text-muted">- {endpoint.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-wt-gold mb-2">Example Request</h4>
              <div className="bg-wt-bg rounded p-3 font-mono text-xs overflow-x-auto">
                <p className="text-wt-text-primary whitespace-pre">{`curl -X GET "${process.env.REACT_APP_BACKEND_URL}/api/external/orders?limit=10" \\
  -H "X-API-Key: wt_live_your_key_here"`}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* API Keys List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-wt-gold" />
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="text-center py-12 bg-wt-paper rounded-lg border border-wt-border">
          <Key className="h-12 w-12 text-wt-text-muted mx-auto mb-4" />
          <p className="text-wt-text-muted">No API keys yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {apiKeys.map((key) => (
            <motion.div
              key={key.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-paper rounded-lg border border-wt-border p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-wt-text-primary">{key.name}</h3>
                    <Badge className={key.is_active 
                      ? 'bg-green-500/20 text-green-400 border-0' 
                      : 'bg-red-500/20 text-red-400 border-0'
                    }>
                      {key.is_active ? 'Active' : 'Revoked'}
                    </Badge>
                  </div>
                  
                  {key.description && (
                    <p className="text-sm text-wt-text-muted mt-1">{key.description}</p>
                  )}
                  
                  <div className="flex items-center gap-2 mt-2">
                    <code className="bg-wt-bg px-2 py-1 rounded text-xs text-wt-gold font-mono">
                      {key.key_prefix}...
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(key.key_prefix)}
                      className="h-6 px-2 text-wt-text-muted"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 mt-3 text-xs text-wt-text-muted">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Created: {formatDate(key.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Activity className="h-3 w-3" />
                      Used: {key.usage_count} times
                    </span>
                    {key.last_used_at && (
                      <span className="flex items-center gap-1">
                        Last: {formatDate(key.last_used_at)}
                      </span>
                    )}
                    {key.expires_at && (
                      <span className="flex items-center gap-1 text-orange-400">
                        <AlertTriangle className="h-3 w-3" />
                        Expires: {formatDate(key.expires_at)}
                      </span>
                    )}
                  </div>
                  
                  {key.permissions?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {key.permissions.map((perm, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-wt-border text-wt-text-muted">
                          {perm}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-1">
                  {key.is_active ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeKey(key.id)}
                      className="text-orange-400 hover:text-orange-300"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleActivateKey(key.id)}
                      className="text-green-400 hover:text-green-300"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteKey(key.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Key Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Create API Key</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-secondary">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Production Integration"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            <div>
              <Label className="text-wt-text-secondary">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="What is this key used for?"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
                rows={2}
              />
            </div>
            
            <div>
              <Label className="text-wt-text-secondary">Permissions</Label>
              <p className="text-xs text-wt-text-muted mb-2">
                Leave empty for full access, or select specific scopes
              </p>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-wt-bg rounded border border-wt-border">
                {Object.entries(availableScopes).map(([scope, desc]) => (
                  <div key={scope} className="flex items-center gap-2">
                    <Checkbox
                      id={scope}
                      checked={formData.permissions.includes(scope)}
                      onCheckedChange={() => togglePermission(scope)}
                    />
                    <label htmlFor={scope} className="text-xs text-wt-text-primary cursor-pointer">
                      {scope}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-secondary">Rate Limit (req/hour)</Label>
                <Input
                  type="number"
                  value={formData.rate_limit}
                  onChange={(e) => setFormData({...formData, rate_limit: parseInt(e.target.value) || 1000})}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              
              <div>
                <Label className="text-wt-text-secondary">Expires In (days)</Label>
                <Input
                  type="number"
                  value={formData.expires_in_days || ''}
                  onChange={(e) => setFormData({...formData, expires_in_days: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="Never"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-wt-border">
              Cancel
            </Button>
            <Button onClick={handleCreateKey} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Key Display Dialog */}
      <Dialog open={newKeyDialogOpen} onOpenChange={setNewKeyDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Check className="h-5 w-5 text-green-400" />
              API Key Created
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-400 text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Save this key now! It won't be shown again.
              </p>
            </div>
            
            <div className="bg-wt-bg rounded-lg p-4">
              <code className="text-wt-gold font-mono text-sm break-all">{newApiKey}</code>
            </div>
            
            <Button
              onClick={() => copyToClipboard(newApiKey)}
              className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy to Clipboard
            </Button>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setNewKeyDialogOpen(false)} className="w-full">
              I've Saved the Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default APIKeysPage;
